import React, { useState } from "react";
import { Mail, Lock, User } from "lucide-react";
import { auth, getStoredUser } from "../lib/api";
import { useToast } from "../hooks/use-toast";

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).filter(Boolean).join(" ") : "Please try again");

// Sign up (username, email, password) or log in. Required before playing anything.
const AuthForm = ({ onAuthed, title = "Sign up to play", subtitle = "Create your free account and get £30 virtual to start." }) => {
  const { toast } = useToast();
  const [mode, setMode] = useState("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (disabled) return;
    setLoading(true);
    try {
      if (mode === "register") {
        const payload = { email: email.trim(), password, username: username.trim() };
        const wasGuest = !!getStoredUser()?.is_guest;
        const u = wasGuest ? await auth.upgrade(payload) : await auth.register(payload);
        if (!wasGuest) {
          localStorage.setItem("fc_welcome", String(u.coins ?? ""));
          window.dispatchEvent(new Event("fc-welcome"));
        }
        onAuthed(u);
      } else {
        onAuthed(await auth.login({ email: email.trim(), password }));
      }
    } catch (e) {
      toast({ title: "Couldn't continue", description: errText(e?.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  const disabled = loading || !email.trim() || password.length < 6 || (mode === "register" && !username.trim());

  return (
    <div className="card-38 p-6 hover:translate-y-0" data-testid="auth-form">
      <h1 className="font-display font-bold text-2xl" data-testid="auth-form-title">{mode === "register" ? title : "Welcome back"}</h1>
      <p className="text-slate-400 text-sm mt-1">{mode === "register" ? subtitle : "Log in to carry on playing."}</p>

      <div className="mt-4" />
      <div className="inline-flex items-center gap-1 rounded-xl bg-white/5 border border-white/10 p-1 w-full">
        {[
          { id: "register", label: "Sign up" },
          { id: "login", label: "Log in" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`auth-tab-${t.id}`}
            onClick={() => setMode(t.id)}
            className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-colors ${mode === t.id ? "bg-[#10B981] text-[#0F172A]" : "text-slate-300 hover:text-white"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-3 mt-4">
        {mode === "register" && (
          <Field icon={User}>
            <input data-testid="auth-username-input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username (e.g. GafferGio)" maxLength={20} className="w-full bg-transparent outline-none text-white placeholder-gray-500" />
          </Field>
        )}
        <Field icon={Mail}>
          <input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="w-full bg-transparent outline-none text-white placeholder-gray-500" />
        </Field>
        <Field icon={Lock}>
          <input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Password (6+ characters)" className="w-full bg-transparent outline-none text-white placeholder-gray-500" />
        </Field>
      </div>

      <button data-testid="auth-submit-btn" onClick={submit} disabled={disabled} className="btn-green w-full rounded-xl py-3.5 font-semibold mt-4 disabled:opacity-50">
        {loading ? "Loading…" : mode === "register" ? "Create account & play" : "Log in"}
      </button>

      <p className="text-slate-500 text-xs text-center mt-3">New accounts get £30 virtual. Virtual money only, no real cash.</p>
    </div>
  );
};

const Field = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3 focus-within:border-[#10B981]/60 transition-colors">
    <Icon className="w-4 h-4 text-slate-500 shrink-0" />
    {children}
  </div>
);

export default AuthForm;
